import { createRouter, createWebHistory } from 'vue-router'

import { useContentStore } from '@/stores/content'
import { useDeployVersionStore } from '@/stores/deployVersion'
import { useMainStore } from '@/stores/main'

import { expireAndReset, isDataExpired, isExpirationExemptRoute } from '@/lib/dataExpiration'
import {
  requireScenesOrSkipAddOns,
  requireSchoolAndCatalog,
  requireSessionScheduled,
  requireSessionType,
  requireSessionTypeForTerms,
  requireSessionTypeNotDirty,
  requireSessionTypeOrSkipPrepay,
  requireTermsAccepted
} from '@/router/guards'
import { routeName } from '@/router/routeName'
import { appErrorService } from '@/services/AppErrorService'

import AppBareLayout from '@/layout/AppBareLayout.vue'
import AppLayout from '@/layout/AppLayout.vue'

import type { BookingPageMetaTitles } from '@VelocitySquared/sunny16-content-dataobjects-cady/booking'

function routeNameToTitleKey(name: string): keyof BookingPageMetaTitles {
  return name.replace(/-([a-z])/g, (_, c) => c.toUpperCase()) as keyof BookingPageMetaTitles
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior() {
    return { top: 0, left: 0 }
  },
  routes: [
    {
      path: '/',
      component: AppLayout,
      children: [
        {
          path: '/',
          name: routeName.LANDING_PAGE,
          component: () => import('@/views/LandingPageView.vue')
        },
        {
          path: '/sessions',
          name: routeName.SESSIONS_LISTING_PAGE,
          component: () => import('@/views/SessionsListingPageView.vue'),
          beforeEnter: requireSchoolAndCatalog
        },
        {
          path: '/session-detail/:slug',
          name: routeName.SESSION_DETAILS,
          component: () => import('@/views/SessionDetailsView.vue'),
          beforeEnter: requireSchoolAndCatalog
        },
        {
          path: '/scenes',
          name: routeName.SCENE_SELECTION,
          component: () => import('@/views/SceneSelectionView.vue'),
          beforeEnter: requireSessionType
        },
        {
          path: '/add-ons',
          name: routeName.ADD_ONS_SELECTION,
          component: () => import('@/views/AddOnsSelectionView.vue'),
          beforeEnter: requireScenesOrSkipAddOns
        },
        {
          path: '/schedule-session',
          name: routeName.SCHEDULE_SESSION,
          component: () => import('@/views/ScheduleSessionView.vue'),
          beforeEnter: requireSessionTypeNotDirty
        },
        {
          path: '/checkout/pre-pay',
          name: routeName.CHECKOUT_PRE_PAY,
          component: () => import('@/views/Checkout/CheckoutPrePayView.vue'),
          beforeEnter: requireSessionTypeOrSkipPrepay
        },
        {
          path: '/checkout/summary',
          name: routeName.CHECKOUT_SUMMARY,
          component: () => import('@/views/Checkout/CheckoutSummaryView.vue'),
          beforeEnter: requireSessionScheduled
        },
        {
          path: '/checkout/terms',
          name: routeName.CHECKOUT_TERMS,
          component: () => import('@/views/Checkout/CheckoutTermsView.vue'),
          beforeEnter: requireSessionTypeForTerms
        },
        {
          path: '/checkout/pay',
          name: routeName.CHECKOUT_PAY,
          component: () => import('@/views/Checkout/CheckoutPayView.vue'),
          beforeEnter: requireTermsAccepted,
          meta: { protectFromMidFlowReset: true }
        },
        {
          path: '/checkout/order-complete',
          name: routeName.ORDER_COMPLETE_STRIPE,
          component: () => import('@/views/Checkout/OrderCompleteStripeView.vue'),
          meta: { protectFromMidFlowReset: true }
        },
        {
          path: '/confirmation/:orderId?/:cartSecurityKey?',
          name: routeName.CONFIRMATION,
          component: () => import('@/views/ConfirmationView.vue'),
          meta: { protectFromMidFlowReset: true }
        },
        {
          path: '/:pathMatch(.*)*',
          name: routeName.NOT_FOUND,
          component: () => import('@/views/NotFoundView.vue')
        }
      ]
    },
    {
      path: '/bare',
      name: 'bare-layout-parent',
      component: AppBareLayout,
      children: [
        // Add routes that use the bare layout here
      ]
    },
    {
      path: '/debug',
      name: 'debug-parent',
      component: AppBareLayout,
      children: [
        {
          path: 'reset-data',
          name: routeName.DEBUG_RESET_DATA,
          component: () => import('@/views/Debug/ResetDataView.vue'),
          meta: { protectFromMidFlowReset: true }
        }
      ]
    }
  ]
})

// Stale-deploy hard-reload at safe navigation boundaries
router.beforeEach((to, _from, next) => {
  const deployStore = useDeployVersionStore()
  if (deployStore.deployStale && !to.meta.protectFromMidFlowReset) {
    window.location.assign(to.fullPath)
    return next(false)
  }
  next()
})

// Data-expiration guard. Runs before the title/loading-state logic so a
// stale-data session is torn down and redirected to Sessions Listing
// instead of briefly rendering the (now-defunct) destination view.
// The pay-flow tail (pay, order-complete-stripe, confirmation) is
// exempt — once the user has reached Stripe, expiring would destroy
// state they have paid for.
router.beforeEach(async (to, from, next) => {
  const mainStore = useMainStore()
  const expiring = mainStore.isExpiringData
  if (!expiring && !isExpirationExemptRoute(to) && isDataExpired(mainStore.dataLoadedAt)) {
    await expireAndReset(router)
    return next(false)
  }

  const titlePrefix = 'CADY Booking'
  const contentStore = useContentStore()
  const title = to.name ? contentStore.pageMetaTitles[routeNameToTitleKey(String(to.name))] : ''
  document.title = title ? `${titlePrefix} - ${title}` : titlePrefix
  mainStore.showSidebar = false

  // While `expireAndReset` is in flight it owns `appLoadingMessage`
  // ("Refreshing your session...") and `isAppProcessing`. Skip only the
  // loading-state writes that would overwrite the expiration message
  // during its own `router.replace` to Sessions Listing — the title and
  // sidebar updates above are safe to run regardless.
  if (!expiring) {
    mainStore.isRouteNavigating = to.meta.showLoading !== false
    mainStore.appLoadingMessage = to.meta.loadingMessage ?? 'Loading...'
  }

  next()
})

// Reset navigation loading state after route change completes
router.afterEach(() => {
  const mainStore = useMainStore()
  mainStore.isRouteNavigating = false
})

// Dynamic import error handling - refresh page on publish
router.onError((err, to) => {
  const mainStore = useMainStore()
  mainStore.isRouteNavigating = false
  const dynamicImportErrors = [
    'failed to fetch dynamically imported module',
    'unable to preload css',
    'importing a module script failed',
    'error loading dynamically imported module',
    'load failed'
  ]

  if (dynamicImportErrors.some((e) => err.message.toLowerCase().includes(e.toLowerCase()))) {
    window.location.href = to.fullPath
    return
  }

  appErrorService.reportVueRouterError(err)
})

export default router
